/**
 * Utility functions for handling dynamic import errors
 * This provides a centralized way to detect and handle chunk loading failures
 */

/**
 * Check if an error is related to dynamic import failures
 * @param error - The error object to check
 * @returns boolean indicating if this is a dynamic import error
 */
export function isDynamicImportError (error: Error): boolean {
    if (!error?.message) return false;

    const errorMessage = error.message.toLowerCase();

    return (
        // Chrome/Edge error messages
        errorMessage.includes('failed to fetch dynamically imported module')
        || errorMessage.includes('loading chunk')
        || errorMessage.includes('loading css chunk')

        // Firefox error messages
        || errorMessage.includes('error loading dynamically imported module')

        // Safari error messages
        || errorMessage.includes('an error occurred trying to load the resource')

        // Network-related errors that could be chunk loading
        || errorMessage.includes('failed to fetch')
        || errorMessage.includes('networkerror')

        // Generic import errors
        || errorMessage.includes('import')
    );
}

/**
 * Handle a dynamic import error by storing the intended route and reloading
 * @param targetRoute - The route the user was trying to navigate to
 */
export function handleDynamicImportError (targetRoute: string): void {
    // Store the intended destination
    sessionStorage.setItem('pendingRoute', targetRoute);

    // Reload to get fresh chunks
    window.location.reload();
}

/**
 * Get and clear any pending route after page reload
 * @returns The pending route or null
 */
export function getPendingRoute (): string | null {
    const pendingRoute = sessionStorage.getItem('pendingRoute');
    if (pendingRoute) {
        sessionStorage.removeItem('pendingRoute');
        return pendingRoute;
    }
    return null;
}
